import React, { useState } from 'react';
import { IFederalState } from '../../Interfaces/IAemter';
import {
  federalStateLoginSchema,
  IFederalStateLogin,
} from '../../Interfaces/IFederalStateLogin';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { CustomeTextField } from '../../generic/CustomeTextField';
import { IUser } from '../../Interfaces/IUser';
import { IUserGroup, selectableUserGroups } from '../../Interfaces/IUserGroup';
import { useCheckFederalStateLogin } from '../../hooks/reactQuerys/FederalStateLoginQueries';

interface IProps {
  normalLogin: IFederalStateLogin;
  setNormalLogin: Function;
  /** Ausgewählter Erstzugang (für den Login-Test). Wird separat angelegt. */
  stepOneLogin: IFederalStateLogin | undefined;
  setStepOneLogin: Function | undefined;
  //
  users: IUser[];
  userGroups: IUserGroup[];
  federalState: IFederalState;
  federalStateLogins: IFederalStateLogin[];
}

/**
 * Formular für einen (persönlichen) Grundbuch-Zugang. Die Erstanmeldung wird
 * NICHT mehr hier angelegt, sondern separat – hier wird sie nur als Erstzugang
 * ausgewählt. Zuordnung erfolgt entweder zu Mitarbeitern ODER zu Gruppen
 * (nicht gemischt). Das Passwort ist optional (nur zum Testen).
 */
export const EditFederalStateLogin: React.FC<IProps> = ({
  federalState,
  normalLogin,
  stepOneLogin,
  setNormalLogin,
  setStepOneLogin,
  users,
  userGroups,
  federalStateLogins,
}) => {
  // Vorhandene Erstanmeldungen dieses Bundeslands (zur Auswahl als Erstzugang)
  const erstanmeldungen = federalStateLogins
    .filter(login => login.idFederalState === federalState.idFederalState)
    .filter(login => login.isFirstStage == true);

  const { checkFederalStateLogin, isPendingCheckFederalStateLogin } =
    useCheckFederalStateLogin();
  const isPending = isPendingCheckFederalStateLogin;

  const [isTesting, setIsTesting] = useState(false);
  const [wasSuccessfully, setWasSuccessfully] = useState(false);

  // Zuordnung: entweder Mitarbeiter ODER Gruppe (gegenseitig ausschließend)
  const [assignmentType, setAssignmentType] = useState<'user' | 'group'>(
    (normalLogin.UserGroupArray?.length ?? 0) > 0 ? 'group' : 'user'
  );

  const switchAssignmentType = (type: 'user' | 'group') => {
    setAssignmentType(type);
    // Die jeweils andere Art leeren (nicht gemischt)
    if (type === 'user') {
      setNormalLogin({ ...normalLogin, UserGroupArray: [] });
    } else {
      setNormalLogin({ ...normalLogin, UserArray: [] });
    }
  };

  const handleTestFullStep = async () => {
    let uploadParentObject: IFederalStateLogin | undefined = undefined;
    if (federalState.isTwoStage == true) {
      uploadParentObject = federalStateLogins.find(
        login =>
          login.idFederalStateLogin === normalLogin.Parent_idFederalStateLogin
      );
    }

    const uploadArray = [federalStateLoginSchema.parse(normalLogin)];
    if (uploadParentObject) {
      uploadArray.push(federalStateLoginSchema.parse(uploadParentObject));
    }

    setIsTesting(true);
    try {
      const returnObject = await checkFederalStateLogin(uploadArray);
      setWasSuccessfully(returnObject['Successfully']);
    } catch (error) {
      console.error(error);
      setWasSuccessfully(false);
    }
  };

  return (
    <>
      <Collapse in={isTesting}>
        {isPending ? (
          <CircularProgress />
        ) : wasSuccessfully ? (
          <Alert severity='success'>Die Anmeldung war erfolgreich!</Alert>
        ) : (
          <Alert severity='warning'>
            Eine Anmeldung war nicht möglich!
            <br />
            Bitte überprüfen Sie Ihre Anmeldeinformationen
          </Alert>
        )}
        <Box sx={{ mb: 2 }} />
      </Collapse>

      <Grid container spacing={2}>
        {/* Notar */}
        <Grid item xs={12}>
          <Typography sx={{ mb: 0.5 }}>
            Bitte wählen Sie den zugeordneten Notar aus.
          </Typography>
          <TextField
            variant='outlined'
            label='Notar'
            fullWidth
            value={
              normalLogin.Notary_idUser < 0 ? '' : normalLogin.Notary_idUser
            }
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setNormalLogin({
                ...normalLogin,
                Notary_idUser: Number(event.target.value),
              });
            }}
            required
            select
            size='small'
          >
            {users
              .filter(user => user.isNotary == true)
              .map(user => (
                <MenuItem key={`user-${user.idUser}`} value={user.idUser}>
                  {`${user.FirstName} ${user.LastName}`}
                </MenuItem>
              ))}
          </TextField>
        </Grid>

        {/* Erstzugang (nur zweistufig) — separat angelegt, hier nur auswählen */}
        {federalState.isTwoStage && (
          <Grid item xs={12}>
            {erstanmeldungen.length === 0 ? (
              <Alert severity='info'>
                Für dieses Bundesland ist eine Erstanmeldung erforderlich. Bitte
                legen Sie zuerst über „Erstanmeldung anlegen" eine Erstanmeldung
                an und wählen Sie sie anschließend hier aus.
              </Alert>
            ) : (
              <TextField
                variant='outlined'
                label='Erstzugang (Erstanmeldung)'
                fullWidth
                value={normalLogin.Parent_idFederalStateLogin ?? ''}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  const idParent =
                    event.target.value === ''
                      ? null
                      : Number(event.target.value);
                  const parent = federalStateLogins.find(
                    l => l.idFederalStateLogin === idParent
                  );
                  setNormalLogin({
                    ...normalLogin,
                    Parent_idFederalStateLogin: idParent,
                  });
                  if (setStepOneLogin) setStepOneLogin(parent);
                }}
                required
                select
                size='small'
              >
                {erstanmeldungen.map(entry => (
                  <MenuItem
                    key={`erst-${entry.idFederalStateLogin}`}
                    value={entry.idFederalStateLogin}
                  >
                    {users.find(u => u.idUser === entry.Notary_idUser)
                      ?.LastName ?? `Notar #${entry.Notary_idUser}`}
                    : {entry.Username}
                  </MenuItem>
                ))}
              </TextField>
            )}
          </Grid>
        )}

        {/* Zugangsdaten */}
        <Grid item xs={12}>
          <CustomeTextField
            attr='Username'
            label='Benutzerkennung'
            object={normalLogin}
            setObject={setNormalLogin}
            type='string'
            required
          />
        </Grid>
        <Grid item xs={12}>
          <CustomeTextField
            attr='SecondName'
            label='Bearbeiterkennzeichen'
            object={normalLogin}
            setObject={setNormalLogin}
            type='string'
            required
          />
        </Grid>
        <Grid item xs={12}>
          <CustomeTextField
            attr='Password'
            label='Passwort (optional, nur zum Testen)'
            object={normalLogin}
            setObject={setNormalLogin}
            type='password'
          />
          <Typography variant='caption' color='text.secondary'>
            Das persönliche Passwort wird nicht gespeichert und ist kein
            Pflichtfeld. Es kann hier zum Testen eingegeben werden und muss beim
            Grundbuchabruf jeweils manuell eingegeben werden.
          </Typography>
        </Grid>

        {/* Zuordnung: Mitarbeiter ODER Gruppe */}
        <Grid item xs={12}>
          <Typography variant='subtitle2'>Zugriff zuordnen</Typography>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 0.5 }}>
            Ordnen Sie diesen Zugang entweder Mitarbeitern oder Gruppen zu
            (nicht gemischt).
          </Typography>
          <RadioGroup
            row
            value={assignmentType}
            onChange={e =>
              switchAssignmentType(e.target.value as 'user' | 'group')
            }
          >
            <FormControlLabel
              value='user'
              control={<Radio />}
              label='Mitarbeiter'
            />
            <FormControlLabel
              value='group'
              control={<Radio />}
              label='Gruppe'
            />
          </RadioGroup>
        </Grid>

        {assignmentType === 'user' ? (
          <Grid item xs={12}>
            <FormControl fullWidth size='small'>
              <InputLabel id='fsl-users-label'>Mitarbeiter</InputLabel>
              <Select
                labelId='fsl-users-label'
                multiple
                input={<OutlinedInput label='Mitarbeiter' />}
                value={normalLogin.UserArray ?? []}
                onChange={event =>
                  setNormalLogin({
                    ...normalLogin,
                    UserArray: event.target.value as number[],
                    UserGroupArray: [],
                  })
                }
                renderValue={selected => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {(selected as number[]).map(id => {
                      const u = users.find(x => x.idUser === id);
                      return (
                        <Chip
                          key={`sel-user-${id}`}
                          size='small'
                          label={u ? `${u.FirstName} ${u.LastName}` : `#${id}`}
                        />
                      );
                    })}
                  </Box>
                )}
              >
                {users.map(u => (
                  <MenuItem key={`mi-user-${u.idUser}`} value={u.idUser}>
                    <Checkbox
                      checked={
                        (normalLogin.UserArray ?? []).indexOf(u.idUser) > -1
                      }
                    />
                    <ListItemText primary={`${u.FirstName} ${u.LastName}`} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        ) : (
          <Grid item xs={12}>
            <FormControl fullWidth size='small'>
              <InputLabel id='fsl-groups-label'>Gruppe</InputLabel>
              <Select
                labelId='fsl-groups-label'
                multiple
                input={<OutlinedInput label='Gruppe' />}
                value={normalLogin.UserGroupArray ?? []}
                onChange={event =>
                  setNormalLogin({
                    ...normalLogin,
                    UserGroupArray: event.target.value as number[],
                    UserArray: [],
                  })
                }
                renderValue={selected => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {(selected as number[]).map(id => {
                      const g = userGroups.find(x => x.idUserGroup === id);
                      return (
                        <Chip
                          key={`sel-group-${id}`}
                          size='small'
                          label={g ? g.UserGroup : `#${id}`}
                        />
                      );
                    })}
                  </Box>
                )}
              >
                {selectableUserGroups(userGroups).map(g => (
                  <MenuItem
                    key={`mi-group-${g.idUserGroup}`}
                    value={g.idUserGroup}
                  >
                    <Checkbox
                      checked={
                        (normalLogin.UserGroupArray ?? []).indexOf(
                          g.idUserGroup
                        ) > -1
                      }
                    />
                    <ListItemText primary={g.UserGroup} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        )}

        {/* Testen */}
        <Grid item xs={12}>
          <Button
            variant='outlined'
            disabled={isPending}
            onClick={handleTestFullStep}
            sx={{ float: 'right' }}
          >
            {isPending ? (
              <Box display='flex' alignItems='center' gap={1}>
                <span>Testen</span>
                <CircularProgress size={20} />
              </Box>
            ) : (
              'Testen'
            )}
          </Button>
        </Grid>
      </Grid>
    </>
  );
};
